// Product gallery: photos switch IN PLACE. All slides sit stacked in one fixed frame, so the backdrop never moves or
// repeats; going to another "page" (front -> back) fades the new photo in over the current one. The pages stay:
// arrow buttons, the "01 / 02" counter, progress segments, arrow keys, and a sideways swipe (touch or mouse drag)
// all change page. Vertical page scrolling on phones still works (the frame only listens for sideways swipes).
// When a colour change swaps the gallery, the new gallery opens on the same page (front stays front, back stays
// back) so that photo decodes in place.
// The last viewed slide index, kept across gallery swaps on this page.
let handoff = 0;

const FADE_MS = 380;
const SWIPE_PX = 40; // how far a sideways swipe must travel to change page

// Stacked-frame rules, added once. Unlayered so they win over the gallery's scroll-strip utility classes.
function addStyles() {
  if (document.getElementById('s-media-stack-style')) return;
  const style = document.createElement('style');
  style.id = 's-media-stack-style';
  style.textContent = `
    [data-product-gallery].is-stacked { display: grid; overflow: hidden; scroll-snap-type: none; touch-action: pan-y; user-select: none; }
    @media (pointer: fine) { [data-product-gallery].is-stacked { cursor: grab; } }
    [data-product-gallery].is-stacked > * { grid-area: 1 / 1; min-width: 0; opacity: 0; visibility: hidden; pointer-events: none; }
    [data-product-gallery].is-stacked > [data-stack-leaving] { opacity: 1; visibility: visible; z-index: 0; }
    [data-product-gallery].is-stacked > [data-stack-active] { opacity: 1; visibility: visible; pointer-events: auto; z-index: 1; }
    [data-product-gallery].is-stacked > [data-stack-active].is-fading { animation: s-stack-in ${FADE_MS}ms ease both; }
    @keyframes s-stack-in { from { opacity: 0; } to { opacity: 1; } }
    @media (prefers-reduced-motion: reduce) { [data-product-gallery].is-stacked > [data-stack-active].is-fading { animation: none; } }
  `;
  document.head.append(style);
}

class SMediaSwipe extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-product-gallery]');
    if (!this.track) return;
    addStyles();
    this.track.classList.add('is-stacked');
    this.prev = this.querySelector('[data-swipe-prev]');
    this.next = this.querySelector('[data-swipe-next]');
    this.counter = this.querySelector('[data-swipe-counter]');
    this.segments = [...this.querySelectorAll('[data-swipe-segment]')];
    this.template = this.querySelector('[data-swipe-counter-template]')?.innerHTML.trim();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.prev?.addEventListener('click', () => this.go(this.index - 1), { signal });
    this.next?.addEventListener('click', () => this.go(this.index + 1), { signal });
    this.track.addEventListener('keydown', (event) => this.onKey(event), { signal });
    this.track.addEventListener('pointerdown', (event) => this.onDragStart(event), { signal });
    this.track.addEventListener('wheel', (event) => this.onWheel(event), { passive: false, signal });
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.track.addEventListener('click', (event) => {
      // Swallow the click that ends a swipe so it doesn't trigger anything on the photo.
      if (this.dragged) {
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
      }
    }, { capture: true, signal });
    // Clicking (desktop) or tapping (phones) the photo shows the next one (the last wraps back to the first, so two photos flip front /
    // back). Not while the binocular zoom is on, where the mouse drives the lens; a drag's click is swallowed above.
    this.track.addEventListener('click', (event) => {
      if (event.button !== 0) return;
      if (document.getElementById('loupe-toggle')?.checked) return;
      if (event.target.closest?.('a, button, input, label, select, textarea')) return;
      const count = this.slides.length;
      if (count < 2) return;
      this.go((this.index + 1) % count);
    }, { signal });

    this.index = Math.min(handoff, Math.max(0, this.slides.length - 1));
    this.show(this.index, false);
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.leaveTimer);
  }

  // Called by s-variant-picker.js after it reveals another colourway's photos in place: re-reads the visible photos
  // and shows the same position (front stays front, back stays back) without a fade.
  refresh() {
    if (!this.track) return;
    this.index = Math.min(this.index, Math.max(0, this.slides.length - 1));
    this.show(this.index, false);
  }

  get slides() {
    return [...this.track.children].filter((child) => !child.hidden);
  }

  get rtl() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  go(index) {
    const count = this.slides.length;
    const target = Math.max(0, Math.min(count - 1, index));
    if (target === this.index) return;
    this.show(target, true);
  }

  // Shows one page: the new photo fades in on top while the old one stays solid underneath until it has, so the
  // backdrop never dims or flickers.
  show(index, animate) {
    const slides = this.slides;
    const current = slides[this.index];
    clearTimeout(this.leaveTimer);
    for (const slide of this.track.children) slide.removeAttribute('data-stack-leaving');
    slides.forEach((slide, i) => {
      const active = i === index;
      slide.toggleAttribute('data-stack-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.classList.remove('is-fading');
    });
    const incoming = slides[index];
    if (animate && current && current !== incoming) {
      current.setAttribute('data-stack-leaving', '');
      void incoming.offsetWidth; // restart the fade if it was mid-way
      incoming.classList.add('is-fading');
      this.leaveTimer = setTimeout(() => {
        current.removeAttribute('data-stack-leaving');
        incoming.classList.remove('is-fading');
      }, FADE_MS + 40);
    }
    this.index = index;
    handoff = index;
    this.sync();
  }

  sync() {
    const count = this.slides.length;
    const index = this.index;
    this.toggle(this.prev, index > 0);
    this.toggle(this.next, index < count - 1);
    this.segments.forEach((segment, i) => {
      segment.classList.toggle('bg-accent', i === index);
      segment.classList.toggle('bg-border', i !== index);
    });
    if (this.counter && this.template) {
      this.counter.textContent = this.template.replace('__CURRENT__', String(index + 1).padStart(2, '0'));
    }
  }

  toggle(button, enabled) {
    if (!button) return;
    button.setAttribute('aria-disabled', String(!enabled));
    button.classList.toggle('opacity-50', !enabled);
  }

  onKey(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const forward = (event.key === 'ArrowRight') !== this.rtl;
    event.preventDefault();
    this.go(this.index + (forward ? 1 : -1));
  }

  // Trackpad two-finger swipe (sideways wheel): the photo follows the swipe, and once it has gone far enough the
  // page changes, one page per swipe (the trackpad's coasting after the flick is ignored until the fingers rest).
  // Up-and-down scrolling is left alone, so the page still scrolls normally.
  onWheel(event) {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    if (document.getElementById('loupe-toggle')?.checked) return;
    event.preventDefault(); // keeps the browser's back/forward swipe from firing over the photo
    const photo = this.slides[this.index]?.querySelector('.group');
    const springBack = (target) => {
      if (!target) return;
      target.style.transition = 'transform 0.25s var(--ease-ui, ease)';
      target.style.transform = '';
      setTimeout(() => target.style.removeProperty('transition'), 280);
    };
    clearTimeout(this.wheelIdle);
    this.wheelIdle = setTimeout(() => {
      this.wheelLocked = false;
      this.wheelSum = 0;
      springBack(this.wheelPhoto);
      this.wheelPhoto = null;
    }, 180);
    if (this.wheelLocked) return;
    this.wheelSum = (this.wheelSum || 0) + event.deltaX * (event.deltaMode === 1 ? 16 : 1);
    const forward = (this.wheelSum > 0) !== this.rtl;
    const count = this.slides.length;
    const edge = (forward && this.index >= count - 1) || (!forward && this.index <= 0);
    if (photo) {
      this.wheelPhoto = photo;
      photo.style.transition = 'none';
      photo.style.transform = `translateX(${-this.wheelSum * (edge ? 0.25 : 0.6)}px)`;
    }
    if (!edge && Math.abs(this.wheelSum) > SWIPE_PX * 2) {
      this.wheelLocked = true;
      springBack(photo);
      this.wheelPhoto = null;
      this.go(this.index + (forward ? 1 : -1));
    }
  }

  // Sideways drag with a mouse, finger or pen: the photo follows the pointer (the backdrop stays put), and letting go
  // past SWIPE_PX changes page (drag left for the next, right for the previous); a shorter drag springs back.
  onDragStart(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // With the binocular zoom on, dragging moves the lens, not the page.
    if (document.getElementById('loupe-toggle')?.checked) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const photo = this.slides[this.index]?.querySelector('.group');
    let moved = 0;
    const { signal } = this.controller;
    const drag = new AbortController();
    if (event.pointerType === 'mouse') event.preventDefault(); // no text selection or image ghost while dragging

    const onMove = (moveEvent) => {
      moved = moveEvent.clientX - startX;
      const vertical = Math.abs(moveEvent.clientY - startY);
      if (!this.dragged && Math.abs(moved) > 6 && Math.abs(moved) > vertical) {
        this.dragged = true;
        this.track.style.cursor = 'grabbing';
        if (photo) photo.style.transition = 'none';
      }
      if (this.dragged && photo) {
        // Follows the pointer, with a little resistance past the first page / last page.
        const count = this.slides.length;
        const forward = (moved < 0) !== this.rtl;
        const edge = (forward && this.index >= count - 1) || (!forward && this.index <= 0);
        photo.style.transform = `translateX(${moved * (edge ? 0.25 : 0.6)}px)`;
      }
    };

    const onUp = () => {
      drag.abort();
      if (!this.dragged) return;
      this.track.style.cursor = '';
      if (photo) {
        photo.style.transition = 'transform 0.25s var(--ease-ui, ease)';
        photo.style.transform = '';
        setTimeout(() => photo.style.removeProperty('transition'), 280);
      }
      if (Math.abs(moved) > SWIPE_PX) {
        const forward = (moved < 0) !== this.rtl;
        this.go(this.index + (forward ? 1 : -1));
      }
      // Clear the flag if no click follows (pointer released outside the frame).
      setTimeout(() => { this.dragged = false; }, 0);
    };

    const all = AbortSignal.any([signal, drag.signal]);
    window.addEventListener('pointermove', onMove, { signal: all });
    window.addEventListener('pointerup', onUp, { signal: all });
    window.addEventListener('pointercancel', onUp, { signal: all });
  }
}

if (!customElements.get('s-media-swipe')) customElements.define('s-media-swipe', SMediaSwipe);
